<template>
  <fts-popper
    :tag="tag"
    :append-to-body="appendToBody"
    :delay-on-mouse-over="delayOnMouseOver"
    :delay-on-mouse-out="delayOnMouseOut"
    :trigger="trigger"
    :placement="placement"
    :strategy-fixed="strategyFixed"
    :prevent-overflow-alt-axis="preventOverflowAltAxis"
    :prevent-overflow-tether-off="preventOverflowTetherOff"
    :prevent-overflow-boundary="preventOverflowBoundary"
    :flip="flip"
    :offset="offset"
    :follow-width="followWidth"
    :content-class="getContentClass"
    :stop-propagation="stopPropagation"
    :prevent-default="preventDefault"
    :reference-id="referenceId"
    :reference-for="referenceFor"
    :overlay="overlay"
    :before-open="beforeOpen"
    :disabled="disabled"
    :z-index="zIndex"
    class="fts-dropdown-select"
    ref="popper"
    @content:keydown-enter="$emit('content:keydown-enter')"
    @content:keydown-up="evt => $emit('content:keydown-up', evt)"
    @content:keydown-down="evt => $emit('content:keydown-down', evt)"
    @select="selectOption"
    @created="created"
    @show="show"
    @hide="hide"
    @content:destroyed="$emit('content:destroyed')"
  >
    <!-- content -->
    <template #content="{select}">
      <!-- menu -->
      <slot
        name="menu"
        :select="select"
        defaultOptionClass="dropdown-select-option"
        defaultDividerClass="dropdown-select-divider"
      >
        <slot name="menu-header" />
        <template v-if="options && options.length">
          <template v-for="(option, index) in options">
            <!-- divider -->
            <div 
              class="dropdown-divider" 
              :key="`divider-${index}`" 
              v-if="option.optionRole === 'divider'" />
            <!-- label -->
            <div 
              class="dropdown-label" 
              :key="`label-${index}`" 
              v-else-if="option.optionRole === 'label'">
              {{option.name || 'Label'}}
            </div>
            <!-- option -->
            <slot 
              name="option" 
              :select="select" 
              :option="option"
              defaultClass="dropdown-select-option" 
              v-else>
              <div class="dropdown-select-option" :key="`${option.id}-${index}`" @click="select(option)">
                <div>{{getOption(option)}}</div>
                <div class="option-description" v-if="getOptionDescription(option)">
                  {{getOptionDescription(option)}}
                </div>
              </div>
            </slot>
          </template>
        </template>
        <!-- no result -->
        <slot name="no-option" defaultClass="dropdown-select-option disabled" v-else>
          <div class="dropdown-select-option disabled">
            No available options found    
          </div>
        </slot>
      </slot>
    </template>
    <!-- trigger -->
    <div slot="reference" class="dropdown-select-toggle" :class="getToggleClass">
      <slot name="toggle" :selectedOption="selectedOption" :selectedOptionValue="selectedOptionValue">
        {{selectedOptionValue}}
      </slot>
    </div>
  </fts-popper>
</template>

<script>
import FTSPopper from "./FTSPopperNew";

import "../../assets/scss/components/dropdown.scss";

export default {  
  props: {
    // popper props
    tag: String,
    appendToBody: Boolean,
    delayOnMouseOver: {
      type: Number,
      default: 10
    },
    delayOnMouseOut: {
      type: Number,
      default: 10
    },
    trigger: {
      type: String,
      default: "click",
    },
    placement: {
      type: String,
      default: "bottom-start"
    },
    strategyFixed: Boolean,
    preventOverflowAltAxis: Boolean,
    preventOverflowTetherOff: Boolean,
    preventOverflowBoundary: String,
    flip: {
      type: Boolean,
      default: true,
    },
    offset: {
      type: [Array, Function],
      default: () => [0, 6],
    },
    followWidth: Boolean,
    contentClass: String,
    appendToggleClass: String,
    appendContentClass: String,
    zIndex: Number,
    stopPropagation: Boolean,
    preventDefault: Boolean,
    referenceId: String,
    referenceFor: String,
    overlay: Boolean,
    beforeOpen: Function,
    disabled: Boolean,
    // dropdown select props
    selectedOption: [Object, Number, String],
    placeholder: {
      type: String,
      default: "Select option"
    },
    options: Array,
    optionKey: {
      type: String,
      default: "name",
    },
    reduce: {
      type: Function,
      default: option => option,
    },
    reduceKey: {
      type: String,
      default: 'id',
    },
    optionDescription: Boolean,
    optionDescriptionKey: {
      type: String,
      default: "description",
    },
  },
  model: {
    prop: 'selectedOption',
    event: 'updateSelectedOption'
  },
  components: {
    "fts-popper": FTSPopper,
  },
  methods: {
    select(val) {
      this.$refs.popper.select(val);
    },
    // events
    selectOption(data) {
      let res = this.reduce(data);
      this.$emit('selected', res),
      this.$emit('updateSelectedOption', res);
    },
    created() {
      this.$emit('created');
    },
    show() {
      this.$emit('show');
    },
    hide() {
      this.$emit('hide');
    },
    // option
    getOption(item) {
      return (item instanceof Object && item.constructor.name === "Object") ? item[this.optionKey] || '(undefined)' : item;
    },
    getOptionDescription(item) {
      return this.optionDescription && item[this.optionDescriptionKey];
    },
  },
  computed: {
    getToggleClass() {
      let toggleClass = !this.disabled ?
        this.trigger === "hover" ? ["hoverable"] : ["clickable"] : [];

      if (this.appendToggleClass)
        toggleClass = toggleClass.concat(this.appendToggleClass);

      return toggleClass.join(" ");
    },
    getContentClass() {
      return this.contentClass ? this.contentClass : `dropdown-select-content dropdown-select-menu ${this.appendContentClass ? this.appendContentClass : ''}`;
    },
    selectedOptionValue() {
      if (this.selectedOption) { 
        if (typeof this.selectedOption === "object" && this.selectedOption[this.optionKey]) {
          return this.selectedOption[this.optionKey];     
        } else if (typeof this.selectedOption !== "object") {
          try {
            const res = this.options && this.options.find(x => x[this.reduceKey] === this.selectedOption);
            if (res && res[this.optionKey]) return res[this.optionKey];
          } catch (err) {
            console.error("DropdownSelect:SelectedOptionValue", err);
          }
          return this.selectedOption;
        }
      }  
      return this.placeholder;
    },
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.dropdown-select-toggle {
  &.hoverable {
    cursor: default;
  }
  &.clickable {
    cursor: pointer;
  }
}
.dropdown-select-content.dropdown-select-menu {
  // default
  max-height: 500px;
  max-width: 250px;
}
.dropdown-select-option {
  display: block;
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
  word-break: break-word;
  color: var(--fts-text);
  text-decoration: none;
  &.disabled {
    color: var(--fts-disabled);
    cursor: default;
  }
  &:hover:not(.disabled) {
    @include theme {
      background-color: rgba(theme-get('fts-gray-100'), 0.75);
    }
    cursor: pointer;
  }
  .option-description {
    margin-top: .135rem;
    font-size: 90%;
    color: var(--fts-default);
  }
}
</style>